import { useRef, useState, type DragEvent } from 'react';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import NoteAddOutlined from '@mui/icons-material/NoteAddOutlined';
import {
  Box,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { FileTypeIcon } from '@/components/shared/file-type-icon';
import {
  fileTypeFromName,
  formatFileSize,
  type FileType,
} from '@/components/shared/file-type-icon/file-type';

// TODO(PRC-7656): migrate the refund flow to this shared component and remove
// the legacy FileUploader at packages/subsidy/.../invoice-form/components/file-uploader.
const MAX_FILE_SIZE_MB = 2;
const ACCEPTED_TYPES: FileType[] = ['pdf', 'jpg', 'png'];
const ACCEPT_ATTRIBUTE = '.pdf,.jpg,.jpeg,.png';

const validateFile = (file: File): string | null => {
  const type = fileTypeFromName(file.name);

  if (!type || !ACCEPTED_TYPES.includes(type)) {
    return 'Formato inválido. Envie um pdf, jpg ou png.';
  }

  if (file.size / 1024 ** 2 > MAX_FILE_SIZE_MB) {
    return 'O arquivo excedeu o tamanho limite de 2mb.';
  }

  return null;
};

interface AttachmentFieldProps {
  value: File | null;
  onChange: (file: File | null) => void;
  id?: string;
  labelId?: string;
}

export const AttachmentField = ({
  value,
  onChange,
  id,
  labelId,
}: AttachmentFieldProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectFile = (file: File | undefined) => {
    if (!file) return;

    const validationError = validateFile(file);

    if (validationError) {
      setError(validationError);
      onChange(null);
      return;
    }

    setError(null);
    onChange(file);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    selectFile(event.target.files?.[0]);
    event.target.value = '';
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    selectFile(event.dataTransfer.files?.[0]);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const remove = () => {
    setError(null);
    onChange(null);
  };

  const openFileDialog = () => inputRef.current?.click();

  const hiddenInput = (
    <input
      ref={inputRef}
      id={id}
      type="file"
      accept={ACCEPT_ATTRIBUTE}
      style={{ display: 'none' }}
      onChange={handleInputChange}
      aria-labelledby={labelId}
    />
  );

  if (value && !error) {
    return (
      <>
        {hiddenInput}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{
            p: 2,
            borderRadius: 2,
            border: ({ palette }) => `1px solid ${palette.neutral[200]}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} minWidth={0}>
            <FileTypeIcon type={fileTypeFromName(value.name)} />
            <Box minWidth={0}>
              <Typography variant="h3" noWrap>
                {value.name}
              </Typography>
              <Typography variant="body2" color="neutral.500">
                {formatFileSize(value.size)}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={remove} aria-label="Remover anexo">
            <DeleteOutline />
          </IconButton>
        </Stack>
      </>
    );
  }

  return (
    <>
      {hiddenInput}
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        role="button"
        tabIndex={0}
        aria-label="Adicionar anexo"
        onClick={openFileDialog}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openFileDialog();
          }
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setIsDragging(false)}
        sx={{
          p: 2,
          borderRadius: 2,
          cursor: 'pointer',
          border: ({ palette }) =>
            `1px solid ${error ? palette.error.main : palette.neutral[200]}`,
          backgroundColor: isDragging ? 'neutral.100' : 'transparent',
        }}
      >
        <NoteAddOutlined sx={{ fontSize: 40, color: 'primary.main' }} />
        <Box>
          <Typography variant="body1">
            Arraste e solte ou{' '}
            <Link
              component="span"
              onClick={(event) => {
                event.stopPropagation();
                openFileDialog();
              }}
            >
              escolha
            </Link>{' '}
            um arquivo
          </Typography>
          <Typography variant="body2" color={error ? 'error' : 'neutral.500'}>
            {error ?? 'adicione um pdf, jpg ou png de até 2mb.'}
          </Typography>
        </Box>
      </Stack>
    </>
  );
};
